import { forwardRef, useState } from 'react';

import Box from '~/common/components/Box';
import TextField from '~/common/mui/Inputs/TextField';
import { hexToRgba } from '~/common/util/format';
import palette from '~/theme/mui/palette';

type PasswordInputMaskingProps = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  id?: string;
  label?: string;
  sx?: React.ComponentProps<typeof Box>['sx'];
};

const asterisksOverlayStyle = (disabled: boolean) => ({
  position: 'absolute',
  top: '12px',
  left: 0,
  height: '100%',
  width: '100%',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '12px',
  fontSize: '1rem',
  color: disabled ? hexToRgba(palette.black, 0.5) : palette.black,
  letterSpacing: '0.1em',
});

const PasswordInputMasking = forwardRef<
  HTMLInputElement,
  PasswordInputMaskingProps
>(
  (
    {
      value,
      onChange,
      error,
      errorMessage,
      disabled,
      isDisabled,
      sx,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <Box sx={{ position: 'relative', ...(sx as Record<string, unknown>) }}>
        <TextField
          {...props}
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              backgroundColor: palette?.white,
            },
            '& input': {
              color: 'transparent',
              textShadow: `0 0 0 ${palette?.black}`,
              caretColor: palette?.black,
              textIndent: value && !isFocused ? '-9999px' : '0px',
            },
            '& input::placeholder': {
              color: hexToRgba(palette.blue[900], 0.42),
              textShadow: 'none',
              opacity: 1,
            },
          }}
          value={value ?? ''}
          id={props.id}
          onChange={onChange}
          error={error}
          errorMessage={errorMessage}
          placeholder="Enter Password"
          disabled={disabled}
          inputRef={ref}
          autoComplete="off"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {!isFocused && (
          <Box
            data-testid="password-overlay"
            sx={asterisksOverlayStyle(disabled || isDisabled)}
          >
            {'•'.repeat(value?.length ?? 0)}
          </Box>
        )}
      </Box>
    );
  },
);

export default PasswordInputMasking;
